"use client";

import React from "react";
import styles from "./premium-save-share.module.css";
import { IoHeartOutline, IoHeart, IoShareSocialOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { resolveCardSavedError } from "@/lib/resolve-listing-card-saved-state";
import { env } from "@/lib/env";
import { ApiError } from "@/types/types";

const LISTINGSENDPOINTURL = env.NEXT_LISTINGS_API_URL as string;

interface PremiumSaveShareProps {
  listingId: string;
  listingTitle: string;
}

export default function PremiumSaveShare({
  listingId,
  listingTitle,
}: PremiumSaveShareProps) {
  const { userData } = useUserAuthDataContext();

  // 🚀 Reuse your global state cache so this stays synced with the rest of the app!
  const isSaved = useListingsCacheStore(
    (state) => state.meta[listingId]?.is_saved,
  );
  const updateSavedState = useListingsCacheStore(
    (state) => state.updateSavedState,
  );

  // --- SAVE LOGIC ---
  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!userData.idToken) {
      toast("Te rugăm să te autentifici pentru a salva proprietatea.", {
        type: "error",
      });
      return;
    }

    const endpoint = isSaved
      ? "/removeFromSavedListings"
      : "/addToSavedListings";
    const attemptedState = !isSaved;

    // Optimistic UI Update
    updateSavedState(listingId, attemptedState);

    try {
      const response = await fetch(`${LISTINGSENDPOINTURL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: userData.idToken,
        },
        body: JSON.stringify({ listing_id: listingId }),
      });

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        resolveCardSavedError({
          apiErr,
          listingId,
          attemptedState,
          updateSavedState,
        });
        throw new Error(apiErr.message);
      }

      toast(
        attemptedState
          ? "Proprietate salvată!"
          : "Proprietate ștearsă din favorite.",
      );
    } catch (err) {
      toast("A apărut o eroare la salvare.", { type: "error" });
    }
  };

  // --- SHARE LOGIC ---
  const handleShareClick = async () => {
    const shareData = {
      title: listingTitle,
      text: "Uită-te la această proprietate pe care am găsit-o!",
      url: window.location.href, // Shares the current page URL
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback for desktop browsers without Native Share API
        await navigator.clipboard.writeText(window.location.href);
        toast("Link-ul a fost copiat în clipboard!", { type: "success" });
      }
    } catch (err) {
      console.error("Error sharing:", err);
    }
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Te interesează această proprietate?</h3>
      <p className={styles.subtitle}>
        Salvează-o pentru mai târziu sau distribuie-o cu cineva drag.
      </p>

      <div className={styles.actionGroup}>
        <button
          className={`${styles.actionBtn} ${isSaved ? styles.saved : ""}`}
          onClick={handleFavoriteClick}
        >
          {isSaved ? (
            <IoHeart className={styles.heartActive} />
          ) : (
            <IoHeartOutline />
          )}
          <span>{isSaved ? "Salvată" : "Salvează"}</span>
        </button>

        <button className={styles.actionBtn} onClick={handleShareClick}>
          <IoShareSocialOutline />
          <span>Distribuie</span>
        </button>
      </div>
    </div>
  );
}
